import React, { useEffect, useState, useRef, useContext } from "react";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import purpleMarker from "./marker-icon-2x-violet.png";
import markerShadow from "./marker-shadow.png";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, MapPin, Filter, ArrowUpDown, ExternalLink } from 'lucide-react';
import { translateCategory } from "../utils/categoryTranslations";
import { saveDiscounts, getCachedDiscounts } from '../utils/offlineDB';

//Remove default icon loaders
delete L.Icon.Default.prototype._getIconUrl;
//Add custom marker icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl: purpleMarker,
  iconUrl: purpleMarker,
  shadowUrl: markerShadow,
});

//to position the user smouthly on the map
const MapFlyTo = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, map.getZoom());
  }, [center, map]);
  return null;
};

//main component
const AllDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  const { user, accessToken, language } = useContext(AuthContext);
  //const [activePopupId, setActivePopupId] = useState(null);

  const [sortVisible, setSortVisible] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const navigate = useNavigate();

  const getStatusLabel = (status) => {
    if (status === "Active") return t.statusActive;
    if (status === "Expired") return t.statusExpired;
    return status;
  };

  //filter options
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterArea, setFilterArea] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterHasPhoto, setFilterHasPhoto] = useState("all");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  //filter dropdowns
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    cities: [],
    areas: []
  });

  const translations = {
    el: {
      viewDiscounts: "Προβολή προσφορών",
      filter: "Φίλτρα",
      sort: "Ταξινόμηση",
      sortBy: "Ταξινόμηση κατά",
      datePublished: "Ημερομηνία δημοσίευσης",
      price: "Τιμή",
      discountName: "Όνομα προσφοράς",
      rating: "Βαθμολογία",
      status: "Κατάσταση",
      storeName: "Όνομα καταστήματος",
      ascending: "Αύξουσα",
      descending: "Φθίνουσα",
      az: "Α-Ω",
      za: "Ω-Α",
      category: "Κατηγορία",
      city: "Πόλη",
      area: "Περιοχή",
      selectCategory: "Επιλογή κατηγορίας",
      selectCity: "Επιλογή πόλης",
      selectArea: "Επιλογή περιοχής",
      loading: "Φόρτωση...",
      noDiscounts: "Δεν βρέθηκαν προσφορές.",
      viewOnMap: "Προβολή στο χάρτη",
      statusActive: "Ενεργή",
      statusExpired: "Λήγμένη",
      noStore: "Χωρίς κατάστημα",
      loadMore: "Φόρτωση περισσότερων...",
      nmDiscounts: "Δεν υπάρχουν άλλες προσφορές",
      statusFilter: "Κατάσταση προσφοράς",
      allStatuses: "Όλες",
      onlyActive: "Μόνο ενεργές",
      onlyExpired: "Μόνο ληγμένες",
      photoFilter: "Φωτογραφία",
      allPhotos: "Όλες",
      withPhoto: "Με φωτογραφία",
      withoutPhoto: "Χωρίς φωτογραφία",
      priceRange: "Εύρος τιμής",
      minPrice: "Από (€)",
      maxPrice: "Έως (€)",
      dateRange: "Εύρος ημερομηνιών",
      dateFrom: "Από",
      dateTo: "Έως"
    },
    en: {
      viewDiscounts: "View offers",
      filter: "Filter",
      sort: "Sort",
      sortBy: "Sort by",
      datePublished: "Date published",
      price: "Price",
      discountName: "Offer name",
      rating: "Rating",
      status: "Status",
      storeName: "Store name",
      ascending: "Ascending",
      descending: "Descending",
      az: "A-Z",
      za: "Z-A",
      category: "Category",
      city: "City",
      area: "Area",
      selectCategory: "Select category",
      selectCity: "Select city",
      selectArea: "Select area",
      loading: "Loading...",
      noDiscounts: "No offers found.",
      viewOnMap: "View offers on map",
      statusActive: "Active",
      statusExpired: "Expired",
      noStore: "No store",
      loadMore: "Loading more...",
      nmDiscounts: "No more offers",
      statusFilter: "Offer status",
      allStatuses: "All",
      onlyActive: "Active only",
      onlyExpired: "Expired only",
      photoFilter: "Photo",
      allPhotos: "All",
      withPhoto: "With photo",
      withoutPhoto: "Without photo",
      priceRange: "Price range",
      minPrice: "From (€)",
      maxPrice: "To (€)",
      dateRange: "Date range",
      dateFrom: "From",
      dateTo: "To"
    }
  };

  const t = translations[language] || translations.el;

  const isAlphabeticalSortField = (field) => ["title", "status", "store_name"].includes(field);
  const getOrderLabel = (value) => {
    if (isAlphabeticalSortField(sortBy)) {
      return value === "asc" ? t.az : t.za;
    }
    return value === "asc" ? t.ascending : t.descending;
  };

  //fetch with filters and pagination
  const fetchDiscounts = async (pageToLoad = 1) => {
    try {
      if (pageToLoad === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const offset = (pageToLoad - 1) * pageSize;
      const queryParams = new URLSearchParams({
        lang: language,
        sortBy,
        order,
        limit: pageSize,
        offset
      });

      if (filterCategory.length > 0) {
        queryParams.append("categories", filterCategory.join(","));
      }

      if (filterCity) {
        queryParams.append("city", filterCity);
      }

      if (filterArea.length > 0) {
        queryParams.append("areas", filterArea.join(","));
      }

      if (filterStatus !== "all") {
        queryParams.append("status", filterStatus);
      }

      if (filterHasPhoto !== "all") {
        queryParams.append("hasPhoto", filterHasPhoto);
      }

      if (filterPriceMin !== "") {
        queryParams.append("priceMin", filterPriceMin);
      }

      if (filterPriceMax !== "") {
        queryParams.append("priceMax", filterPriceMax);
      }

      if (filterDateFrom) {
        queryParams.append("dateFrom", filterDateFrom);
      }

      if (filterDateTo) {
        queryParams.append("dateTo", filterDateTo);
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/discounts/alldiscounts?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        console.error(data);
        if (pageToLoad === 1) setDiscounts([]);
        setHasMore(false);
        return;
      }

      const fetched = data.discounts || [];
      if (pageToLoad === 1) {
        setDiscounts(fetched);
        setIsOffline(false);
        saveDiscounts(fetched).catch(() => { });
      } else {
        setDiscounts(prev => [...prev, ...fetched]);
      }

      setHasMore(fetched.length === pageSize);
      if (pageToLoad === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }

    } catch (err) {
      console.error(err);
      if (pageToLoad === 1) {
        const cached = await getCachedDiscounts();
        if (cached.length > 0) {
          setDiscounts(cached);
          setIsOffline(true);
        }
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user || !accessToken) return;
    setDiscounts([]);
    setPage(1);
    setHasMore(true);
    fetchDiscounts(1);
  }, [
    filterCategory,
    filterCity,
    filterArea,
    filterStatus,
    filterHasPhoto,
    filterPriceMin,
    filterPriceMax,
    filterDateFrom,
    filterDateTo,
    language,
    user,
    accessToken,
    sortBy,
    order
  ]);

  useEffect(() => {
    if (!user || !accessToken) return;
    if (page === 1) return;
    fetchDiscounts(page);
  }, [page, user, accessToken]);


  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/discounts/filter-options?lang=${language}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        const json = await res.json();
        setFilterOptions({
          categories: json.categories || [],
          cities: json.cities || [],
          areas: []
        });

      } catch (err) {
        console.error("Failed to fetch filters", err);
      }
    };

    if (accessToken) fetchFilters();
  }, [accessToken, language]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prev) => prev + 1);
      }
    }, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);

  const initialCenter = (() => {
    const first = discounts?.find(
      d => d.full_store?.latitude && d.full_store?.longitude
    );
    return first
      ? { lat: Number(first.full_store.latitude), lng: Number(first.full_store.longitude) }
      : { lat: 37.9838, lng: 23.7275 }; // Athens fallback
  })();

  const handleClickListItem = d => {
    navigate("/viewdiscount", {
      state: {
        discountId: d.discount_id,
      },
    });
  };

  const toggleSelection = (value, state, setState) => {
    if (state.includes(value)) {
      setState(state.filter(v => v !== value));
    } else {
      setState([...state, value]);
    }
  };

  //to fix the map view after the filters
  const MapResizeFix = ({ deps = [] }) => {
    const map = useMap();
    useEffect(() => {
      setTimeout(() => {
        map.invalidateSize();
      }, 0);
    }, deps);

    return null;
  };
  //to center the map depending on the filters
  useEffect(() => {
    if (filterCity || filterArea) {
      const firstMatch = discounts.find(d => d.full_store?.latitude && d.full_store?.longitude);
      if (firstMatch) {
        setSelectedCenter({
          lat: Number(firstMatch.full_store.latitude),
          lng: Number(firstMatch.full_store.longitude)
        });
      }
    } else {
      setSelectedCenter(initialCenter);
    }
  }, [filterCity, filterArea, discounts, language]);

  //load areas
  useEffect(() => {
    const fetchAreas = async () => {
      if (!filterCity) {
        setFilterOptions(prev => ({ ...prev, areas: [] }));
        return;
      }
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/discounts/areasByCity?city=${filterCity}&lang=${language}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const json = await res.json();

        setFilterOptions(prev => ({
          ...prev,
          areas: json.areas || []
        }));

      } catch (err) {
        console.error("Failed to fetch areas", err);
      }
    };

    if (accessToken) fetchAreas();

  }, [filterCity, language, accessToken]);

  //grouping discounts based on the store
  const groupedStores = Object.values(
    discounts.reduce((acc, discount) => {
      const store = discount.full_store;

      //if no store or no coords -> skip
      if (!store?.latitude || !store?.longitude) {
        return acc;
      }

      const storeId = store.store_id;

      if (!acc[storeId]) {
        acc[storeId] = {
          store_id: storeId,
          store_name_el: store.store_name_el,
          store_name_en: store.store_name_en,
          latitude: Number(store.latitude),
          longitude: Number(store.longitude),
          discounts: []
        };
      }

      acc[storeId].discounts.push(discount);
      return acc;
    }, {})
  );

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />

      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />

        {/* main*/}
        <main id="discounts-content" className="flex flex-col flex-1 justify-start px-4 md:px-10 py-10 xl:ml-20">
          <div className="flex flex-col xl:flex-row gap-10 h-full">

            {/* left list */}
            <aside className="order-2 xl:order-1 xl:w-1/2 w-full xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto">

              <h2 className="text-2xl font-semibold mb-4 text-custom-letters flex justify-between items-center gap-2 w-full max-w-[calc(100%-1rem)] mx-auto overflow-visible">
                <span className="flex items-center gap-2">
                  <ShoppingCart className="text-custom-grad1" /> {t.viewDiscounts}
                </span>

                <div className="flex items-center gap-2">

                  <button
                    onClick={() => setFiltersVisible(prev => !prev)}
                    title={t.filter}
                    className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full flex items-center justify-center"
                  >
                    <Filter size={18} />
                  </button>

                  <button
                    onClick={() => setSortVisible(prev => !prev)}
                    title={t.sort}
                    className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full flex items-center justify-center"
                  >
                    <ArrowUpDown size={18} />
                  </button>

                </div>
              </h2>


              {sortVisible && (
                <div className="bg-custom-header p-4 rounded-xl shadow mb-6 w-full max-w-[calc(100%-1rem)] mx-auto">

                  <p className="text-sm font-semibold mb-2 text-custom-letters">
                    {t.sortBy}
                  </p>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full p-2 rounded border mb-3"
                  >
                    <option value="title">{t.discountName}</option>
                    <option value="created_at">{t.datePublished}</option>
                    <option value="status">{t.status}</option>
                    <option value="store_name">{t.storeName}</option>
                    <option value="rating">{t.rating}</option>
                    <option value="discount_price">{t.price}</option>
                  </select>

                  <select
                    value={order}
                    onChange={(e) => setOrder(e.target.value)}
                    className="w-full p-2 rounded border"
                  >
                    <option value="asc">{getOrderLabel("asc")}</option>
                    <option value="desc">{getOrderLabel("desc")}</option>
                  </select>

                </div>
              )}
              {filtersVisible && (
                <div className="bg-custom-header p-6 rounded-xl shadow mb-6 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-[calc(100%-1rem)] mx-auto">

                  {/* Category Filter*/}
                  <div>
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.category}</p>
                    <select
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value && !filterCategory.includes(value)) {
                          setFilterCategory([...filterCategory, value]);
                        }
                        e.target.value = "";
                      }}
                      className="w-full p-2 rounded border"
                    >
                      <option value="">{t.selectCategory}</option>
                      {filterOptions.categories?.map(cat => (
                        <option key={cat.category_id} value={cat.category_id}>
                          {language === 'el' ? cat.category_name_el : cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.statusFilter}</p>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full p-2 rounded border"
                    >
                      <option value="all">{t.allStatuses}</option>
                      <option value="Active">{t.onlyActive}</option>
                      <option value="Expired">{t.onlyExpired}</option>
                    </select>
                  </div>

                  {/* City Filter */}
                  <div>
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.city}</p>
                    <select
                      value={filterCity}
                      onChange={(e) => {
                        setFilterCity(e.target.value);
                        setFilterArea([]);
                      }}
                      className="w-full p-2 rounded border"
                    >
                      <option value="">{t.selectCity}</option>
                      {filterOptions.cities?.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {/* Area Filter */}
                  <div>
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.area}</p>
                    <select
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value && !filterArea.includes(value)) {
                          setFilterArea([...filterArea, value]);
                        }
                      }}
                      className={`w-full p-2 rounded border ${!filterCity ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={!filterCity}
                    >
                      <option value="">{t.selectArea}</option>
                      {filterOptions.areas?.map(area => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>

                  {/* Photo Filter */}
                  <div>
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.photoFilter}</p>
                    <select
                      value={filterHasPhoto}
                      onChange={(e) => setFilterHasPhoto(e.target.value)}
                      className="w-full p-2 rounded border"
                    >
                      <option value="all">{t.allPhotos}</option>
                      <option value="yes">{t.withPhoto}</option>
                      <option value="no">{t.withoutPhoto}</option>
                    </select>
                  </div>

                  {/* Price Range Filter */}
                  <div>
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.priceRange}</p>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={t.minPrice}
                        value={filterPriceMin}
                        onChange={(e) => setFilterPriceMin(e.target.value)}
                        className="w-1/2 p-2 rounded border text-sm"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={t.maxPrice}
                        value={filterPriceMax}
                        onChange={(e) => setFilterPriceMax(e.target.value)}
                        className="w-1/2 p-2 rounded border text-sm"
                      />
                    </div>
                  </div>

                  {/* Date Range Filter */}
                  <div className="sm:col-span-2">
                    <p className="text-sm font-semibold mb-2 text-custom-letters">{t.dateRange}</p>
                    <div className="flex gap-2 flex-wrap">
                      <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                        <label className="text-xs text-custom-letters">{t.dateFrom}</label>
                        <input
                          type="date"
                          value={filterDateFrom}
                          max={filterDateTo || undefined}
                          onChange={(e) => setFilterDateFrom(e.target.value)}
                          className="p-2 rounded border text-sm w-full"
                        />
                      </div>
                      <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                        <label className="text-xs text-custom-letters">{t.dateTo}</label>
                        <input
                          type="date"
                          value={filterDateTo}
                          min={filterDateFrom || undefined}
                          onChange={(e) => setFilterDateTo(e.target.value)}
                          className="p-2 rounded border text-sm w-full"
                        />
                      </div>
                    </div>
                  </div>

                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-3 mb-6">
                {/* Category pill*/}
                {filterCategory.map(catId => {
                  const categoryObj = filterOptions.categories?.find(
                    c => String(c.category_id) === String(catId)
                  );

                  if (!categoryObj) return null;

                  return (
                    <span
                      key={catId}
                      onClick={() => toggleSelection(catId, filterCategory, setFilterCategory)}
                      className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-sm"
                    >
                      {language === 'el' ? categoryObj.category_name_el : categoryObj.category_name} ✕
                    </span>
                  );
                })}

                {/* City pill*/}
                {filterCity && (
                  <span
                    onClick={() => {
                      setFilterCity("");
                      setFilterArea([]);
                    }}
                    className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm"
                  >
                    {filterCity} ✕
                  </span>
                )}

                {/* Area pill*/}
                {filterArea.map(area => (
                  <span
                    key={area}
                    onClick={() => toggleSelection(area, filterArea, setFilterArea)}
                    className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm"
                  >
                    {area} ✕
                  </span>
                ))}

                {/* Status pill */}
                {filterStatus !== "all" && (
                  <span
                    onClick={() => setFilterStatus("all")}
                    className="px-3 py-1 bg-custom-grad4 text-white rounded-full cursor-pointer text-sm"
                  >
                    {filterStatus === "Active" ? t.onlyActive : t.onlyExpired} ✕
                  </span>
                )}

                {/* Photo pill */}
                {filterHasPhoto !== "all" && (
                  <span
                    onClick={() => setFilterHasPhoto("all")}
                    className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-sm"
                  >
                    {filterHasPhoto === "yes" ? t.withPhoto : t.withoutPhoto} ✕
                  </span>
                )}

                {/* Price range pill */}
                {(filterPriceMin !== "" || filterPriceMax !== "") && (
                  <span
                    onClick={() => { setFilterPriceMin(""); setFilterPriceMax(""); }}
                    className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm"
                  >
                    €{filterPriceMin || "0"} – €{filterPriceMax || "∞"} ✕
                  </span>
                )}

                {/* Date range pill */}
                {(filterDateFrom || filterDateTo) && (
                  <span
                    onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); }}
                    className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm"
                  >
                    {filterDateFrom || "…"} → {filterDateTo || "…"} ✕
                  </span>
                )}

              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="text-center">{t.loading}</div>
                ) : discounts.length === 0 ? (
                  <div className="text-center italic">{t.noDiscounts}</div>
                ) : (
                  discounts.map(d => (
                    <button
                      key={d.discount_id}
                      onClick={() => handleClickListItem(d)}
                      className="w-full max-w-[calc(100%-1rem)] bg-custom-header p-4 rounded-xl shadow cursor-pointer hover:bg-white transition flex flex-col md:flex-row justify-between items-start gap-4 text-left min-w-0"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-4 min-w-0">
                          <span className="text-xl font-semibold text-custom-letters min-w-0 break-words">
                            {language === 'el' ? d.title : (d.title_en || d.title)}
                          </span>

                          {d.discount_price && (
                            <span className="text-xl font-bold text-custom-grad4 whitespace-nowrap flex-shrink-0">
                              €{Number(d.discount_price).toFixed(2)}
                            </span>
                          )}
                        </div>
                        {/* Location & Store Info */}
                        {d.full_store ? (
                          <div className="text-xs text-custom-letters font-medium mt-1">
                            <span>
                              {language === 'el'
                                ? (d.full_store.store_name_el || d.full_store.store_name)
                                : (d.full_store.store_name_en || d.full_store.store_name)}
                            </span>

                            {/* City and Area */}
                            {(d.full_store.city || d.full_store.city_el || d.full_store.area || d.full_store.area_el) && (
                              <span className="text-custom-letters ml-1">
                                • {
                                  [
                                    language === 'el'
                                      ? (d.full_store.area_el || d.full_store.area)
                                      : (d.full_store.area_en || d.full_store.area),
                                    language === 'el'
                                      ? (d.full_store.city_el || d.full_store.city)
                                      : (d.full_store.city_en || d.full_store.city)
                                  ].filter(Boolean).join(", ")
                                }
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-custom-letters mt-1">{t.noStore}</div>
                        )}
                        {d.created_at && (
                          <div className="text-xs text-custom-letters font-medium mt-0.5">
                            {new Date(d.created_at).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        )}
                        <div className="text-base text-custom-letters mt-1">
                          <span className="text-lg leading-none">⭐</span> {d.rating ?? "N/A"}
                        </div>
                        <div className={`text-sm font-semibold rounded-full px-3 py-1 inline-block mt-2 md:hidden ${d.status === 'Active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                          {getStatusLabel(d.status)}
                        </div>
                      </div>
                      <div className="hidden md:flex flex-col items-end gap-4 flex-shrink-0 min-w-0">
                        <div className={`text-sm font-semibold rounded-full px-3 py-1 h-fit ${d.status === 'Active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                          {getStatusLabel(d.status)}
                        </div>
                        <div className="w-24 h-24 rounded-md overflow-hidden bg-custom-background border border-custom-letters/20 flex-shrink-0">
                          {d.photo_url ? (
                            <img
                              src={d.photo_url}
                              alt={language === 'el' ? d.title : (d.title_en || d.title)}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-custom-letters text-center px-2">
                              No image
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <div ref={loaderRef} className="h-10" />
              {loadingMore && <div className="text-center mt-2">{t.loadMore}</div>}
              {!hasMore && !loading && discounts.length > 0 && (
                <div className="text-center mt-2">{t.nmDiscounts}</div>
              )}

              {/* <div className="flex justify-center items-center gap-4 mt-6">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="px-4 py-2 rounded-full bg-custom-grad1 text-custom-letters disabled:opacity-50"
                >
                  Previous
                </button>

                <span className="font-semibold text-custom-letters">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="px-4 py-2 rounded-full bg-custom-grad4 text-custom-letters disabled:opacity-50"
                >
                  Next
                </button>
              </div> */}
            </aside>

            {/* right map */}
            <section className="order-1 xl:order-2 xl:w-1/2 w-full flex flex-col max-h-full xl:sticky xl:top-24">
              <h2 id="discounts-map-title" className="text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2">
                <MapPin className="text-custom-grad1" /> {t.viewOnMap}
              </h2>

              <div className="bg-custom-header/70 rounded-xl shadow p-3 h-[300px] xl:h-[70vh] xl:min-h-0">
                <MapContainer
                  center={initialCenter}
                  zoom={18}
                  className="rounded-xl h-full w-full"
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapFlyTo center={selectedCenter} />
                  <MapResizeFix deps={[discounts]} />

                  {groupedStores.map((store) => (
                    <Marker
                      key={store.store_id}
                      position={[store.latitude, store.longitude]}
                    >
                      <Popup>
                        <div className="min-w-[220px] max-h-[300px] overflow-y-auto">
                          <h3 className="font-semibold mb-2">
                            {language === "el"
                              ? store.store_name_el
                              : store.store_name_en}
                          </h3>

                          <ul className="space-y-2">
                            {store.discounts.map((d) => (
                              <li
                                key={d.discount_id}
                                className="border rounded-md px-3 py-2 text-sm flex justify-between items-center"
                              >
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium text-custom-letters">
                                      {language === 'el' ? d.title : (d.title_en || d.title)}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`/viewdiscount?id=${d.discount_id}`, '_blank');
                                      }}
                                      title={language === 'el' ? 'Προβολή προσφοράς' : 'View offer'}
                                      className="text-custom-grad1 hover:text-custom-grad4 transition flex-shrink-0"
                                    >
                                      <ExternalLink size={13} />
                                    </button>
                                  </div>
                                  <div className="text-xs text-gray-400 line-through">
                                    €{Number(d.original_price).toFixed(2)}
                                  </div>
                                </div>

                                <div className="text-custom-grad4 font-semibold">
                                  €{Number(d.discount_price).toFixed(2)}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </section>

          </div>
        </main>
      </div>
    </div>
  );
};

export default AllDiscounts;
